import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, CircularProgress } from '@mui/material';
import { Search, ShoppingCart, CheckCircle, Calendar, MapPin, Navigation } from 'lucide-react';
import { BrandListing } from '../../types/brandlistingsmodel';
import { getAllBrandListings } from '../../services/Brandlistings.api';
import { useCart } from '../../context/CartContext';
import { PageHeader } from '../../components/common/UIComponents';
import { SearchFilterForm, SearchFiltersValue, defaultSearchFilters } from './SearchFilterForm';

const trafficColors: Record<string,{ bg:string; color:string }> = {
  'Very High': { bg:'#e8f5ee', color:'#1a7a4a' },
  'High':      { bg:'#e8f5ee', color:'#1a7a4a' },
  'Medium':    { bg:'#fef6e4', color:'#92610a' },
  'Low':       { bg:'#f3f0ea', color:'#6b5e4e' },
};
const defaultTraffic = { bg:'#f3f0ea', color:'#6b5e4e' };

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
};

/** Builds a clickable Google Maps link from a listing's coordinates. */
const googleMapsUrl = (lat: number, lng: number) => `https://www.google.com/maps?q=${lat},${lng}`;

const SpaceCard: React.FC<{ listing:BrandListing; onView:()=>void; onAddCart:()=>void; inCart:boolean }> = ({ listing, onView, onAddCart, inCart }) => {
  const trafficLevel = listing.locationType?.trafficLevel;
  const tc = trafficLevel ? (trafficColors[trafficLevel] || defaultTraffic) : defaultTraffic;
  const photo = listing.photos?.[0];
  const { totalPayable, providerRent, gstAmount, gstPercent } = listing.priceBreakdown;

  return (
    <div className="card-hover" style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:18, overflow:'hidden', cursor:'pointer', boxShadow:'var(--shadow-sm)' }}>
      <div style={{ position:'relative', height:185, overflow:'hidden' }} onClick={onView}>
        {photo ? (
          <img src={photo} alt={listing.title} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.4s ease' }}
            onMouseEnter={e=>(e.currentTarget as HTMLImageElement).style.transform='scale(1.04)'}
            onMouseLeave={e=>(e.currentTarget as HTMLImageElement).style.transform='scale(1)'}/>
        ) : (
          <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-muted)', color:'var(--text-muted)', fontFamily:'DM Sans', fontSize:12 }}>
            No photo
          </div>
        )}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)' }}/>
        {listing.locationType?.name && (
          <div style={{ position:'absolute', top:12, left:12, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(6px)', color:'#fff', padding:'3px 10px', borderRadius:20, fontSize:10, fontFamily:'DM Sans', fontWeight:700 }}>
            {listing.locationType.name.toUpperCase()}
          </div>
        )}
        <div style={{ position:'absolute', top:12, right:12, background:'rgba(255,255,255,0.92)', backdropFilter:'blur(6px)', color:'var(--text-primary)', padding:'3px 10px', borderRadius:20, fontSize:11, fontFamily:'DM Sans', fontWeight:600, textTransform:'capitalize' }}>
          {listing.mediaType}
        </div>
      </div>

      <div style={{ padding:'16px' }}>
        <h3 onClick={onView} style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)', margin:'0 0 5px', lineHeight:1.35, cursor:'pointer' }}>{listing.title}</h3>

        {/* Google Maps link built from latitude/longitude — opens Maps in a new tab, doesn't trigger card navigation */}
        
        <a  href={googleMapsUrl(listing.latitude, listing.longitude)}
          target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ display:'flex', alignItems:'center', gap:4, color:'var(--text-muted)', fontSize:12, marginBottom:6, textDecoration:'none', width:'fit-content' }}
        >
          <MapPin size={12} color="var(--brand)"/> {listing.area}, {listing.city}
          <Navigation size={10} color="var(--brand)" style={{ marginLeft:2 }}/>
        </a>

        <div style={{ display:'flex', alignItems:'center', gap:4, color:'var(--text-muted)', fontSize:11.5, marginBottom:10 }}>
          <Calendar size={11} color="var(--brand)"/> {formatDate(listing.availableFrom)} – {formatDate(listing.availableTo)}
        </div>

        <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
          <span style={{ background:'var(--bg-muted)', color:'var(--text-secondary)', padding:'2px 9px', borderRadius:6, fontSize:11, fontWeight:500, fontFamily:'DM Sans' }}>{listing.dimensions}</span>
          <span style={{ background:tc.bg, color:tc.color, padding:'2px 9px', borderRadius:6, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>{trafficLevel || 'Standard'} Traffic</span>
        </div>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
          <div>
            <div style={{ color:'var(--text-muted)', fontSize:11, marginBottom:1 }}>total payable (incl. GST)</div>
            <div style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:20 }}>₹{totalPayable.toLocaleString('en-IN')}</div>
            {/* <div style={{ color:'var(--text-muted)', fontSize:10 }}>Rent ₹{providerRent.toLocaleString('en-IN')} + GST ({gstPercent}%) ₹{gstAmount.toLocaleString('en-IN')}</div> */}
          </div>
          <button onClick={inCart ? ()=>{} : onAddCart}
            style={{ background:inCart?'var(--success-bg)':'linear-gradient(135deg,#e8621a,#c2490e)', color:inCart?'var(--success)':'#fff', border: inCart?'1px solid #b8e8ce':'none', borderRadius:10, padding:'8px 14px', fontFamily:'DM Sans', fontWeight:600, fontSize:12, cursor:inCart?'default':'pointer', display:'flex', alignItems:'center', gap:6, boxShadow:inCart?'none':'var(--shadow-brand)', transition:'all 0.2s' }}>
            {inCart?<><CheckCircle size={13}/>Added</>:<><ShoppingCart size={13}/>Add to Cart</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SearchSpaces: React.FC = () => {
  const [listings, setListings] = useState<BrandListing[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState(''); // applied search text — filtered client-side against the current result set
  const { addToCart, items } = useCart();
  const navigate = useNavigate();
  const cartIds = items.map((i: any) => i.space.id);

  const load = async (filters: SearchFiltersValue) => {
    setLoading(true);
    setError('');
    try {
      const result = await getAllBrandListings({
        state: filters.state || undefined,
        city: filters.city || undefined,
        mediaType: filters.mediaType || undefined,
        minRent: filters.minRent,
        maxRent: filters.maxRent,
      });
      setListings(result.listings);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch listings.');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch once on mount, with no filters applied.
  useEffect(() => { load(defaultSearchFilters); }, []);

  // Only re-fetches when SearchFilterForm's Search button (or Enter) fires this.
  const handleSearch = (filters: SearchFiltersValue) => {
    setKeyword(filters.search);
    load(filters);
  };

  const visibleListings = keyword
    ? listings.filter(l =>
        l.title.toLowerCase().includes(keyword.toLowerCase()) ||
        l.area.toLowerCase().includes(keyword.toLowerCase()) ||
        l.city.toLowerCase().includes(keyword.toLowerCase())
      )
    : listings;

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Search Ad Spaces" subtitle={`${total} locations available across India`}
        action={<div style={{ background:'var(--brand-light)', border:'1px solid var(--brand-mid)', borderRadius:10, padding:'6px 14px', fontSize:13, color:'var(--brand)', fontWeight:600, fontFamily:'DM Sans' }}>
          {total} results
        </div>}/>

      <SearchFilterForm onSearch={handleSearch} />

      {loading && (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}>
          <CircularProgress size={28} sx={{ color:'var(--brand)' }} />
        </div>
      )}

      {!loading && error && (
        <div style={{ textAlign:'center', padding:'60px 20px', color:'var(--danger)', fontFamily:'DM Sans,sans-serif', fontSize:13.5 }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <Grid container spacing={2.5}>
          {visibleListings.map(l => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={l.id}>
              <SpaceCard listing={l} onView={()=>navigate(`/brand/spaces/${l.id}`)} onAddCart={()=>addToCart(l as any)} inCart={cartIds.includes(l.id)}/>
            </Grid>
          ))}
          {visibleListings.length === 0 && (
            <Grid item xs={12}>
              <div style={{ textAlign:'center', padding:'60px 20px' }}>
                <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--bg-muted)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:16, color:'var(--text-muted)' }}><Search size={28}/></div>
                <h3 style={{ color:'var(--text-primary)', fontWeight:600, fontSize:17, margin:'0 0 6px' }}>No spaces found</h3>
                <p style={{ color:'var(--text-muted)', fontSize:13 }}>Try adjusting your filters and search again.</p>
              </div>
            </Grid>
          )}
        </Grid>
      )}
    </div>
  );
};

export default SearchSpaces;